/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect } from "react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { Slider } from "@/shared/components/ui/slider";
import { LoadingSpinner } from "@/shared/components/ui/loading-spinner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { Save, RotateCcw, AlertCircle } from "lucide-react";
import { PlaygroundConfigService, PlaygroundConfig } from "../services/playgroundConfigService";
import { useToast } from "@/shared/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface PlaygroundConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
}

// Configuração padrão para evitar erros de undefined
const defaultConfig: PlaygroundConfig = {
  rag: {
    similarityThreshold: 0.7,
    maxResults: 10,
    temperature: 0.7,
    maxTokens: 1000,
    roleSystem: "Você é um assistente útil e prestativo.",
    metadataFilters: {}
  },
  ui: {
    welcomeMessage: "Olá! Como posso ajudá-lo hoje?",
    placeholder: "Digite sua mensagem aqui...",
    maxChatHistory: 100
  },
  restrictions: {
    allowedFileTypes: ["pdf", "txt", "docx"],
    maxQueryLength: 1000,
    rateLimitPerMinute: 10
  }
};

export default function PlaygroundConfigDialog({ isOpen, onClose, workspaceId }: PlaygroundConfigDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<PlaygroundConfig | null>(null);
  const [formData, setFormData] = useState<PlaygroundConfig>(defaultConfig);

  // Carregar configurações ao abrir o diálogo
  useEffect(() => {
    if (isOpen && workspaceId) {
      loadConfig();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, workspaceId]);

  const loadConfig = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await PlaygroundConfigService.getConfig(workspaceId);
      
      // Mesclar dados do backend com configuração padrão para evitar undefined
      const mergedConfig: PlaygroundConfig = {
        rag: {
          ...defaultConfig.rag,
          ...(response.data?.rag || {})
        },
        ui: {
          ...defaultConfig.ui,
          ...(response.data?.ui || {})
        },
        restrictions: {
          ...defaultConfig.restrictions,
          ...(response.data?.restrictions || {})
        }
      };
      
      setConfig(mergedConfig);
      setFormData(mergedConfig);
    } catch (error: any) {
      console.error('Erro ao carregar configurações:', error);
      setError(error.message || t('playground.errors.loadConfig'));
      // Em caso de erro, manter a configuração padrão
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData) return;

    try {
      setIsSaving(true);
      setError(null);
      await PlaygroundConfigService.updateConfig(workspaceId, formData);
      setConfig(formData);
      toast({
        title: t('playground.config.saved'),
        description: t('playground.config.savedDescription'),
      });
      onClose();
    } catch (error: any) {
      console.error('Erro ao salvar configurações:', error);
      setError(error.message || t('playground.errors.saveConfig'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (config) {
      setFormData({ ...config });
    }
  };

  const updateFormData = (section: keyof PlaygroundConfig, field: string, value: any) => {
    if (!formData) return;
    
    setFormData(prev => ({
      ...prev!,
      [section]: {
        ...prev![section],
        [field]: value
      }
    }));
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{t('playground.config.title')}</span>
          </DialogTitle>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-4">
              <LoadingSpinner size="md" />
              <p className="text-sm text-muted-foreground">{t('playground.config.loading')}</p>
            </div>
          </div>
        ) : formData ? (
          <Tabs defaultValue="rag" className="space-y-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="rag">{t('playground.config.tabs.rag')}</TabsTrigger>
              <TabsTrigger value="ui">{t('playground.config.tabs.ui')}</TabsTrigger>
              <TabsTrigger value="restrictions">{t('playground.config.tabs.restrictions')}</TabsTrigger>
            </TabsList>

            <TabsContent value="rag" className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">{t('playground.config.rag.title')}</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('playground.config.rag.similarityThreshold')}</Label>
                    <div className="space-y-2">
                      <Slider
                        value={[formData?.rag?.similarityThreshold || 0.7]}
                        onValueChange={([value]: number[]) => updateFormData('rag', 'similarityThreshold', value)}
                        min={0}
                        max={1}
                        step={0.01}
                        className="w-full"
                      />
                      <div className="text-sm text-muted-foreground text-center">
                        {(formData?.rag?.similarityThreshold || 0.7).toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('playground.config.rag.maxResults')}</Label>
                    <Input
                      type="number"
                      min={1}
                      max={20}
                      value={formData?.rag?.maxResults || 10}
                      onChange={(e) => updateFormData('rag', 'maxResults', parseInt(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('playground.config.rag.temperature')}</Label>
                    <div className="space-y-2">
                      <Slider
                        value={[formData?.rag?.temperature || 0.7]}
                        onValueChange={([value]: number[]) => updateFormData('rag', 'temperature', value)}
                        min={0}
                        max={2}
                        step={0.1}
                        className="w-full"
                      />
                      <div className="text-sm text-muted-foreground text-center">
                        {(formData?.rag?.temperature || 0.7).toFixed(1)}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('playground.config.rag.maxTokens')}</Label>
                    <Input
                      type="number"
                      min={100}
                      max={8000}
                      value={formData?.rag?.maxTokens || 1000}
                      onChange={(e) => updateFormData('rag', 'maxTokens', parseInt(e.target.value))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('playground.config.rag.systemPrompt')}</Label>
                  <Textarea
                    value={formData?.rag?.roleSystem || "Você é um assistente útil e prestativo."}
                    onChange={(e) => updateFormData('rag', 'roleSystem', e.target.value)}
                    placeholder={t('playground.config.rag.systemPromptPlaceholder')}
                    className="min-h-[120px]"
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="ui" className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">{t('playground.config.ui.title')}</h3>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('playground.config.ui.welcomeMessage')}</Label>
                    <Textarea
                      value={formData?.ui?.welcomeMessage || "Olá! Como posso ajudá-lo hoje?"}
                      onChange={(e) => updateFormData('ui', 'welcomeMessage', e.target.value)}
                      placeholder={t('playground.config.ui.welcomeMessagePlaceholder')}
                      className="min-h-[80px]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('playground.config.ui.inputPlaceholder')}</Label>
                    <Input
                      value={formData?.ui?.placeholder || "Digite sua mensagem aqui..."}
                      onChange={(e) => updateFormData('ui', 'placeholder', e.target.value)}
                      placeholder={t('playground.config.ui.inputPlaceholderPlaceholder')}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('playground.config.ui.maxChatHistory')}</Label>
                    <Input
                      type="number"
                      min={10}
                      max={1000}
                      value={formData?.ui?.maxChatHistory || 100}
                      onChange={(e) => updateFormData('ui', 'maxChatHistory', parseInt(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="restrictions" className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">{t('playground.config.restrictions.title')}</h3>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t('playground.config.restrictions.allowedFileTypes')}</Label>
                    <Input
                      value={(formData?.restrictions?.allowedFileTypes || ['pdf', 'txt', 'docx']).join(', ')}
                      onChange={(e) => updateFormData('restrictions', 'allowedFileTypes', 
                        e.target.value.split(',').map(type => type.trim()).filter(Boolean)
                      )}
                      placeholder={t('playground.config.restrictions.allowedFileTypesPlaceholder')}
                    />
                    <p className="text-sm text-muted-foreground">
                      {t('playground.config.restrictions.allowedFileTypesHelp')}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('playground.config.restrictions.maxQueryLength')}</Label>
                    <Input
                      type="number"
                      min={100}
                      max={10000}
                      value={formData?.restrictions?.maxQueryLength || 1000}
                      onChange={(e) => updateFormData('restrictions', 'maxQueryLength', parseInt(e.target.value))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>{t('playground.config.restrictions.rateLimitPerMinute')}</Label>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={formData?.restrictions?.rateLimitPerMinute || 10}
                      onChange={(e) => updateFormData('restrictions', 'rateLimitPerMinute', parseInt(e.target.value))}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        ) : null}

        <DialogFooter>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={isLoading || isSaving}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {t('playground.config.reset')}
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isSaving}
            >
              {t('playground.config.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={isLoading || isSaving}
            >
              {isSaving ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2 scale-75" />
                  {t('playground.config.saving')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {t('playground.config.save')}
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
